import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { plannedSegments, plannedHours, plannedProjection, editorSegments, newEditorSegment } from './plannedSegments';
import { useAuth } from '../../context/AuthContext';
import { canWrite } from '../../utils/roles';
import { fixedScheduleForDate, personMatchesName } from '../../utils/personnel';
import RRHHNativeShell from './RRHHNativeShell';
import { rrhhCoreGet, rrhhCorePost } from './rrhhCoreApi';
import {
  addIsoDays,
  compactTime,
  currentIsoDate,
  formatDisplayDate,
  formatHours,
  formatMoney,
  isAdministrativeOperationalRole,
  isActivePerson,
  monthRange,
  normalizeSearchText,
  normalizeStatus,
  normalizeTime,
  pad,
  pairMarksByStartDay,
  pairTotalHours,
  personDisplayName,
  personOperationalRole,
} from './rrhhUtils';
import { exportPlanDayJpg, exportPlanRangeJpg } from './planExport';
import './RRHHNative.css';

const STATUS_OPTIONS = [
  { value: 'NORMAL', label: 'Jornada normal' },
  { value: 'VACIO', label: 'Vacío' },
  { value: 'LIBRE', label: 'Libre' },
  { value: 'LICENCIA', label: 'Licencia' },
  { value: 'SUSPENDIDO', label: 'Suspendido' },
  { value: 'LIC. MEDICA', label: 'Lic. médica' },
  { value: 'AUSENTE', label: 'Ausente' },
];

const STATUS_CONTEXT_OPTIONS = STATUS_OPTIONS.filter((option) => option.value !== 'NORMAL');
const DEFAULT_WEEK_MIN_OFFSET = -4;
const DEFAULT_WEEK_MAX_OFFSET = 4;
const HISTORICAL_WEEK_BATCH = 52;

const STATUS_META = {
  VACIO: { label: 'VACIO', className: 'empty' },
  LIBRE: { label: 'LIBRE', className: 'free' },
  LICENCIA: { label: 'LICENCIA', className: 'license' },
  SUSPENDIDO: { label: 'SUSPENDIDO', className: 'suspended' },
  'LIC. MEDICA': { label: 'LIC. MEDICA', className: 'medical' },
  AUSENTE: { label: 'AUSENTE', className: 'absent' },
};

const ROLE_GROUPS = [
  { key: 'logisticos', label: 'Logísticos', match: (role) => role.includes('logistic') },
  { key: 'referentes', label: 'Referentes', match: (role) => role.includes('referente') },
  { key: 'operadores', label: 'Operadores', match: (role) => role.includes('operador') },
  { key: 'deposito', label: 'Depósito', match: (role) => role.includes('depo') || role.includes('deposito') },
  { key: 'mantenimiento', label: 'Mantenimiento', match: (role) => role.includes('mant') },
  { key: 'admin', label: 'Admin', match: (role) => role.includes('admin') && !isAdministrativeOperationalRole(role) },
  { key: 'administracion', label: 'Administración', match: (role) => isAdministrativeOperationalRole(role) },
];

function roleGroupForPerson(person) {
  const role = normalizeSearchText(personOperationalRole(person));
  return ROLE_GROUPS.find((group) => group.match(role)) || { key: 'otros', label: personOperationalRole(person) || 'Otros' };
}

function roleSortValue(person) {
  const group = roleGroupForPerson(person);
  const index = ROLE_GROUPS.findIndex((item) => item.key === group.key);
  return index === -1 ? 999 : index;
}

function planDateKey(value) {
  // PostgreSQL entrega DATE como ISO con hora al serializarlo. La grilla usa
  // fechas civiles YYYY-MM-DD, por lo que ambas fuentes deben compartir clave.
  const text = String(value || '');
  const match = text.match(/^\d{4}-\d{2}-\d{2}/);
  return match ? match[0] : text;
}

function cellId(personName, date) {
  return `${personName}|${planDateKey(date)}`;
}

function getMonday(date = new Date()) {
  const copy = new Date(date);
  const day = copy.getDay() || 7;
  copy.setHours(0, 0, 0, 0);
  copy.setDate(copy.getDate() - day + 1);
  return copy;
}

function addDays(date, amount) {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + amount);
  return copy;
}

function isoDate(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function isWeekendIsoDate(date) {
  const day = new Date(`${date}T12:00:00`).getDay();
  return day === 0 || day === 6;
}

function rateForIsoDate(rates = [], date, fallback = 0) {
  const key = String(date || '').slice(0, 10);
  const match = rates.find((rate) => {
    const from = String(rate.valid_from || '').slice(0, 10);
    const to = String(rate.valid_to || '').slice(0, 10);
    return from && from <= key && (!to || to >= key);
  });
  return Number(match?.hourly_rate ?? fallback ?? 0);
}

function weekDays(offset) {
  const monday = addDays(getMonday(new Date()), offset * 7);
  return Array.from({ length: 7 }, (_, index) => {
    const date = addDays(monday, index);
    return {
      date,
      iso: isoDate(date),
      short: date.toLocaleDateString('es-UY', { weekday: 'short' }).replace('.', '').toUpperCase(),
      long: date.toLocaleDateString('es-UY', { weekday: 'long' }),
      day: date.getDate(),
      month: date.toLocaleDateString('es-UY', { month: 'long' }).toUpperCase(),
    };
  });
}

function weekLabel(days, offset) {
  const from = days[0];
  const to = days[6];
  const month = from.date.toLocaleDateString('es-UY', { month: 'short' });
  const endMonth = to.date.toLocaleDateString('es-UY', { month: 'short' });
  const base = `${from.day} ${month} - ${to.day} ${endMonth}`;
  return offset === 0 ? `Semana actual · ${base}` : base;
}

function turnKey(personName, date) {
  return cellId(personName, date);
}

function personnelTurnKey(personnelId, date) {
  const id = Number(personnelId);
  return Number.isInteger(id) && id > 0 ? turnKey(`personnel:${id}`, date) : null;
}

function personTurnKey(person, date) {
  return personnelTurnKey(person?.id ?? person?.persona_external_id ?? person?.persona_id, date)
    || turnKey(personDisplayName(person), date);
}

function setTurnInMap(map, turn, person = null) {
  if (!turn?.fecha) return;
  const personnelId = person?.id ?? turn.persona_external_id ?? turn.persona_id ?? turn.personnel_id;
  const canonicalKey = personnelTurnKey(personnelId, turn.fecha);
  const displayName = person ? personDisplayName(person) : turn.persona;
  if (canonicalKey) map.set(canonicalKey, turn);
  // Fallback temporal para acciones que todavía llegan con el alias como texto.
  if (displayName) map.set(turnKey(displayName, turn.fecha), turn);
}

function statusFromTurn(turn) {
  const status = normalizeStatus(turn?.estado || 'VACIO');
  return STATUS_META[status] ? status : 'NORMAL';
}

function displayShift(turn) {
  if (!turn) return { status: 'VACIO', label: 'VACIO', className: 'empty', hours: '', activity: '', location: '', locationShort: '' };
  const status = statusFromTurn(turn);
  if (status !== 'NORMAL') {
    const meta = STATUS_META[status] || STATUS_META.VACIO;
    return { status, label: meta.label, className: meta.className, hours: '', activity: '', location: '', locationShort: '' };
  }
  const segments = plannedSegments(turn).map((segment, index) => ({
    hours: `${compactTime(segment.hora_inicio)} - ${compactTime(segment.hora_fin)}`,
    label: segment.actividad_ubicacion || 'LOGISTICA',
    location: segment.ubicacion || '',
    locationShort: segment.ubicacion_corta || (index === 0 && segment.ubicacion === turn.ubicacion
      ? turn.ubicacion_corta || segment.ubicacion : segment.ubicacion) || '',
  }));
  return {
    status,
    label: turn.actividad_ubicacion || 'LOGISTICA',
    className: 'normal',
    hours: segments.map((segment) => segment.hours).join(' / '),
    segments,
    activity: turn.actividad_ubicacion || 'LOGISTICA',
    location: turn.ubicacion || '',
    locationShort: turn.ubicacion_corta || turn.ubicacion || '',
  };
}

function emptyEditor(person, day, turn, { forceNormal = false } = {}) {
  const currentStatus = statusFromTurn(turn);
  const status = forceNormal && currentStatus !== 'NORMAL' ? 'NORMAL' : currentStatus;
  return {
    personExternalId: person?.id || turn?.persona_external_id || '',
    personName: personDisplayName(person),
    role: personOperationalRole(person),
    date: day.iso,
    status,
    segments: (currentStatus === 'NORMAL' ? plannedSegments(turn) : [{
      hora_inicio: '09:00', hora_fin: '17:00', actividad_ubicacion: 'LOGISTICA',
    }]).map((segment) => ({
      start: normalizeTime(segment.hora_inicio), end: normalizeTime(segment.hora_fin),
      activity: segment.actividad_ubicacion,
      location: segment.ubicacion || '',
    })),
  };
}

function editorPayload(editor) {
  const status = normalizeStatus(editor.status || 'VACIO');
  const isNormal = status === 'NORMAL';
  const segments = isNormal ? editorSegments(editor.segments) : [];
  return {
    persona_external_id: editor.personExternalId || null,
    persona: editor.personName,
    rol_operativo: editor.role || 'Operador',
    fecha: editor.date,
    estado: isNormal ? 'NORMAL' : status,
    ...plannedProjection(segments),
    tramos_previstos: segments,
    ubicacion: isNormal ? segments[0]?.ubicacion || null : null,
    modificado: true,
  };
}

function shiftPayloadForTarget(source, person, day) {
  const status = normalizeStatus(source?.estado || 'VACIO');
  const isNormal = status === 'NORMAL';
  return {
    persona_external_id: person?.id || null,
    persona: personDisplayName(person),
    rol_operativo: personOperationalRole(person) || source?.rol_operativo || 'Operador',
    fecha: day.iso,
    estado: isNormal ? 'NORMAL' : status,
    hora_inicio: isNormal ? normalizeTime(source?.hora_inicio) || null : null,
    hora_fin: isNormal ? normalizeTime(source?.hora_fin) || null : null,
    actividad_ubicacion: isNormal ? String(source?.actividad_ubicacion || 'LOGISTICA').trim().toUpperCase() : null,
    ubicacion: isNormal ? String(source?.ubicacion || '').trim() || null : null,
    tramos_previstos: isNormal ? plannedSegments(source).map((segment) => ({ ...segment })) : [],
    modificado: true,
  };
}

function rangeSelectionForPerson(personName, days, fromDate, toDate) {
  const fromIndex = days.findIndex((day) => day.iso === fromDate);
  const toIndex = days.findIndex((day) => day.iso === toDate);
  if (fromIndex === -1 || toIndex === -1) return new Set([cellId(personName, toDate)]);
  const start = Math.min(fromIndex, toIndex);
  const end = Math.max(fromIndex, toIndex);
  return new Set(days.slice(start, end + 1).map((day) => cellId(personName, day.iso)));
}

const DEFAULT_RRHH_AI_RULES = Object.freeze({
  sources: ['events', 'tasks', 'armados'],
  phases: ['armado', 'evento', 'desarmado'],
  ignoreNames: ['x', 'sinnombre'],
  activitySources: ['section', 'place', 'name'],
  scheduleMode: 'phase',
  conflictMode: 'review',
  matchMode: 'normalize',
});

function normalizePlanActivity(value) {
  return String(value || '').trim().toUpperCase();
}

function aiNormalizeName(value) {
  return normalizeSearchText(value).replace(/[^a-z0-9]/g, '');
}

function normalizeRuleToken(value) {
  return normalizeSearchText(value)
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function splitRuleValues(value) {
  return String(value || '').split(/[,;|]/).map((item) => item.trim()).filter(Boolean);
}

function normalizeRulePhase(value) {
  const token = normalizeRuleToken(value);
  if (['armado', 'montaje', 'setup', 'carga'].includes(token)) return 'armado';
  if (['evento', 'operacion', 'operativo', 'operacion_evento'].includes(token)) return 'evento';
  if (['desarmado', 'desmontaje', 'teardown', 'descarga'].includes(token)) return 'desarmado';
  return '';
}

function normalizeActivitySource(value) {
  const token = normalizeRuleToken(value);
  if (['lugar', 'ubicacion', 'place', 'venue'].includes(token)) return 'place';
  if (['evento', 'nombre_evento', 'nombre', 'event', 'name'].includes(token)) return 'name';
  if (['cliente', 'client'].includes(token)) return 'client';
  if (['rol', 'role'].includes(token)) return 'role';
  if (['sector', 'seccion', 'section'].includes(token)) return 'section';
  return '';
}

function normalizeSuggestionSource(value) {
  const token = normalizeRuleToken(value);
  if (['eventos', 'evento', 'events', 'event'].includes(token)) return 'events';
  if (['tareas', 'tarea', 'tasks', 'task'].includes(token)) return 'tasks';
  if (['armados', 'armado', 'desarmados', 'desarmado', 'schedule', 'setup'].includes(token)) return 'armados';
  return '';
}

function parseRrhhAiRules(text) {
  const rules = {
    sources: [...DEFAULT_RRHH_AI_RULES.sources],
    phases: [...DEFAULT_RRHH_AI_RULES.phases],
    ignoreNames: [...DEFAULT_RRHH_AI_RULES.ignoreNames],
    activitySources: [...DEFAULT_RRHH_AI_RULES.activitySources],
    scheduleMode: DEFAULT_RRHH_AI_RULES.scheduleMode,
    conflictMode: DEFAULT_RRHH_AI_RULES.conflictMode,
    matchMode: DEFAULT_RRHH_AI_RULES.matchMode,
  };

  String(text || '').split(/\r?\n/).forEach((rawLine) => {
    const line = rawLine.trim();
    if (!line || line.startsWith('#')) return;
    const separatorIndex = line.indexOf(':');
    if (separatorIndex < 0) return;
    const key = normalizeRuleToken(line.slice(0, separatorIndex));
    const values = splitRuleValues(line.slice(separatorIndex + 1));
    const tokens = values.map(normalizeRuleToken);

    if (['fuentes', 'origenes', 'sources'].includes(key)) {
      const sources = values.map(normalizeSuggestionSource).filter(Boolean);
      if (sources.length) rules.sources = [...new Set(sources)];
      return;
    }
    if (['fases', 'fases_de_gestion', 'phases'].includes(key)) {
      const phases = values.map(normalizeRulePhase).filter(Boolean);
      if (phases.length) rules.phases = [...new Set([...rules.phases, ...phases])];
      return;
    }
    if (['ignorar_personas', 'personas_ignoradas', 'ignore_names'].includes(key)) {
      const ignored = values.map((value) => aiNormalizeName(value)).filter(Boolean);
      if (ignored.length) rules.ignoreNames = [...new Set([...rules.ignoreNames, ...ignored])];
      return;
    }
    if (['nombre_turno', 'texto_turno', 'actividad', 'activity'].includes(key)) {
      const sources = values.map(normalizeActivitySource).filter(Boolean);
      if (sources.length) rules.activitySources = [...new Set(sources)];
      return;
    }
    if (['horarios', 'horario', 'schedule'].includes(key)) {
      if (tokens.some((token) => ['usar_horario_evento', 'evento', 'event'].includes(token))) rules.scheduleMode = 'event';
      if (tokens.some((token) => ['usar_horario_por_fase', 'por_fase', 'fase', 'phase'].includes(token))) rules.scheduleMode = 'phase';
      return;
    }
    if (['conflictos', 'conflict'].includes(key)) {
      if (tokens.some((token) => ['ignorar_conflicto', 'ignorar', 'overwrite', 'pisar'].includes(token))) rules.conflictMode = 'ignore';
      if (tokens.some((token) => ['marcar_conflicto', 'revisar', 'review'].includes(token))) rules.conflictMode = 'review';
    }
  });

  return rules;
}

async function parentAppGet(path) {
  const response = await fetch(path, { credentials: 'include', headers: { Accept: 'application/json' } });
  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }
  if (!response.ok) {
    const error = new Error(payload?.error || 'No se pudo leer datos del sistema madre');
    error.status = response.status;
    throw error;
  }
  return payload;
}

async function loadRrhhAiRules() {
  try {
    const payload = await parentAppGet('/api/settings/rrhh_ai_rules');
    return parseRrhhAiRules(payload?.value || '');
  } catch {
    return parseRrhhAiRules('');
  }
}

function normalizeAiEventSource(event) {
  return {
    ...event,
    sourceType: 'events',
    sourceId: `event-${event.id}`,
    sourceLabel: 'Evento',
    name: event.name || 'Evento',
    place: event.place || '',
    start_time: event.start_time || '',
    end_time: event.end_time || '',
  };
}

function normalizeAiTaskSource(task) {
  return {
    ...task,
    sourceType: 'tasks',
    sourceId: `task-${task.id}`,
    sourceLabel: 'Tarea',
    name: task.title || task.event_name || 'Tarea',
    place: task.event_name || task.owner || '',
    start_time: task.time || '',
    end_time: task.end_time || '',
  };
}

function normalizeAiArmadoSource(armado) {
  const isTeardown = normalizeRuleToken(armado.type) === 'desarmado';
  return {
    ...armado,
    sourceType: 'armados',
    sourceId: `armado-${armado.id}`,
    sourceLabel: isTeardown ? 'Desarmado' : 'Armado',
    name: armado.event_name || (isTeardown ? 'Desarmado' : 'Armado'),
    place: armado.place || armado.event_place || '',
    start_time: armado.start_time || '',
    end_time: armado.end_time || '',
  };
}

async function loadParentPlanningSourcesForDay(date, rules) {
  const sourceSet = new Set(rules.sources?.length ? rules.sources : DEFAULT_RRHH_AI_RULES.sources);
  const [events, tasks, armados] = await Promise.all([
    sourceSet.has('events') ? parentAppGet(`/api/events?from=${encodeURIComponent(date)}&to=${encodeURIComponent(date)}`) : Promise.resolve([]),
    sourceSet.has('tasks') ? parentAppGet(`/api/tasks?from=${encodeURIComponent(date)}&to=${encodeURIComponent(date)}`) : Promise.resolve([]),
    sourceSet.has('armados') ? parentAppGet(`/api/armados?from=${encodeURIComponent(date)}&to=${encodeURIComponent(date)}`) : Promise.resolve([]),
  ]);
  return [
    ...events.map(normalizeAiEventSource),
    ...tasks.map(normalizeAiTaskSource),
    ...armados.map(normalizeAiArmadoSource),
  ];
}

function eventAssignments(source) {
  return Array.isArray(source?.staff_assignments) ? source.staff_assignments : [];
}

function shouldUseAssignmentForPlan(assignment, rules, source = null) {
  const phase = normalizeRulePhase(assignment?.phase || 'evento');
  if (!rules.phases.includes(phase)) return false;
  const personName = aiNormalizeName(assignment?.person_name || '');
  if (!personName) return false;
  if (source?.sourceType === 'armados' && !rules.sources.includes('armados')) return false;
  if (source?.sourceType === 'tasks' && !rules.sources.includes('tasks')) return false;
  if (source?.sourceType === 'events' && !rules.sources.includes('events')) return false;
  return !rules.ignoreNames.some((ignored) => ignored && personName === ignored);
}

function sectionForAssignment(source, assignment) {
  const sections = Array.isArray(source?.staff_sections) ? source.staff_sections : [];
  const assignmentSection = String(assignment?.section || 'General').trim();
  return sections.find((section) => (
    normalizeRulePhase(section.phase) === normalizeRulePhase(assignment?.phase || 'evento') &&
    normalizeSearchText(section.name) === normalizeSearchText(assignmentSection)
  )) || null;
}

function sectionLabel(section, assignment) {
  const explicit = String(section?.title || section?.name || assignment?.section || '').trim();
  if (!explicit || normalizeSearchText(explicit) === 'general') return '';
  return explicit;
}

function timeRangeFromText(value) {
  const raw = String(value || '').trim();
  if (!raw) return { start: '', end: '' };
  const match = raw.match(/(\d{1,2}(?::\d{2})?)\s*(?:-|a|hasta)\s*(\d{1,2}(?::\d{2})?)/i);
  if (match) return { start: normalizeTime(match[1]), end: normalizeTime(match[2]) };
  const single = raw.match(/\b(\d{1,2}(?::\d{2})?)\b/);
  if (single) return { start: normalizeTime(single[1]), end: '' };
  return { start: '', end: '' };
}

function timeFromRrhhRules(source, assignment, rules, section = null) {
  const sectionTime = timeRangeFromText(section?.time_text);
  if (sectionTime.start || sectionTime.end) return sectionTime;
  const phase = normalizeRulePhase(assignment?.phase || 'evento');
  if (source.sourceType === 'events' && rules.scheduleMode === 'phase') {
    if (phase === 'armado' && (source.setup_start || source.setup_end)) return { start: source.setup_start, end: source.setup_end };
    if (phase === 'desarmado' && (source.teardown_start || source.teardown_end)) return { start: source.teardown_start, end: source.teardown_end };
  }
  return { start: source.start_time, end: source.end_time };
}

function phaseLabelForSuggestion(phase) {
  const phaseKey = normalizeRulePhase(phase);
  return { armado: 'Armado', evento: 'Operación', desarmado: 'Desarmado' }[phaseKey] || phaseKey || 'Gestión';
}

function suggestionActivityLabel(source, assignment, section = null) {
  const sectionText = sectionLabel(section, assignment);
  const haystack = normalizeSearchText([
    source.sourceLabel,
    source.name,
    source.place,
    source.client,
    sectionText,
    assignment?.section,
    assignment?.role,
  ].filter(Boolean).join(' '));
  if (/\belias\b/.test(haystack)) return 'ELIAS';
  if (/\b(service|depo|deposito|mantenimiento|taller)\b/.test(haystack)) return 'DEPOSITO';
  return normalizePlanActivity(source.place || sectionText || source.name || 'OPERACION');
}

function suggestionHeadingLabel(source, assignment) {
  if (source.sourceType === 'events') return `${phaseLabelForSuggestion(assignment.phase)} ${source.name || 'Evento'}`.trim();
  return source.name || source.sourceLabel || 'Tarea';
}

function findPlanPersonByAssignment(people, name) {
  const target = aiNormalizeName(name);
  if (!target) return null;
  return people.find((person) => personMatchesName(person, name)) || people.find((person) => {
    const personName = aiNormalizeName(personDisplayName(person));
    return personName === target || personName.includes(target) || target.includes(personName);
  }) || null;
}

async function loadPlanDirectory() {
  const response = await fetch('/api/personnel/plan-directory', { credentials: 'include' });
  const payload = await response.json().catch(() => null);
  if (!response.ok) throw new Error(payload?.error || 'No se pudo cargar Personal');
  return Array.isArray(payload) ? payload : [];
}

function turnMapForDirectory(rows, people) {
  const map = new Map();
  rows.forEach((turn) => {
    // La relación del plan es siempre por personnel_id. El alias es solo una
    // etiqueta de interfaz y puede cambiar sin convertir un turno en VACIO.
    const turnPersonnelId = Number(turn.persona_id ?? turn.personnel_id);
    const person = Number.isInteger(turnPersonnelId) && turnPersonnelId > 0
      ? people.find((candidate) => Number(candidate.id) === turnPersonnelId)
      : people.find((candidate) => personMatchesName(candidate, turn.persona));
    const personName = person ? personDisplayName(person) : turn.persona;
    if (!personName || !turn.fecha) return;
    setTurnInMap(map, { ...turn, persona: personName, persona_external_id: turnPersonnelId }, person);
  });
  return map;
}

function fixedSchedulePayloads(people, days, existingTurns) {
  const currentWeekStart = weekDays(0)[0].iso;
  if (days[0].iso < currentWeekStart) return [];
  return people.flatMap((person) => days.flatMap((day) => {
    const personName = personDisplayName(person);
    if (!personName || existingTurns.has(personTurnKey(person, day.iso))) return [];
    if (isWeekendIsoDate(day.iso)) {
      return [{
        persona_external_id: person?.id || null,
        persona: personName,
        rol_operativo: personOperationalRole(person) || 'Operador',
        fecha: day.iso,
        estado: 'LIBRE',
        hora_inicio: null,
        hora_fin: null,
        actividad_ubicacion: null,
        modificado: false,
      }];
    }
    const schedule = fixedScheduleForDate(person, day.iso);
    if (!schedule) return [];
    return [{
      persona_external_id: person?.id || null,
      persona: personName,
      rol_operativo: personOperationalRole(person) || 'Operador',
      fecha: day.iso,
      estado: 'NORMAL',
      hora_inicio: schedule.start,
      hora_fin: schedule.end,
      actividad_ubicacion: schedule.activity || 'LOGISTICA',
      ubicacion: String(schedule.location || schedule.ubicacion || '').trim() || null,
      modificado: false,
    }];
  }));
}

function parsePublishedPlanDates(rows = []) {
  const row = rows.find((item) => item.clave === 'published_plan_dates');
  try {
    const parsed = Array.isArray(row?.valor) ? row.valor : JSON.parse(row?.valor || '[]');
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
}

function existingTurnLabel(turn) {
  if (!turn) return '';
  const shift = displayShift(turn);
  if (shift.status !== 'NORMAL') return shift.label;
  return `${shift.hours} ${shift.activity}`.trim();
}

function buildAiSuggestion({ source, assignment, day, people, turns, rules }) {
  const section = sectionForAssignment(source, assignment);
  const person = findPlanPersonByAssignment(people, assignment.person_name);
  if (!person) return null;
  const planPersonName = personDisplayName(person);
  const scheduledTime = timeFromRrhhRules(source, assignment, rules, section);
  const start = normalizeTime(scheduledTime.start || '');
  const end = normalizeTime(scheduledTime.end || '');
  const existing = turns.get(personTurnKey(person, day.iso));
  const hasExisting = rules.conflictMode === 'review' && existing ? statusFromTurn(existing) !== 'VACIO' : false;
  return {
    id: [source.sourceId, assignment.id || assignment.person_name, assignment.phase, assignment.section, day.iso].map((item) => String(item || '')).join('-'),
    dayIso: day.iso,
    sourceId: source.sourceId,
    sourceType: source.sourceType,
    sourceLabel: source.sourceLabel,
    phaseLabel: phaseLabelForSuggestion(assignment.phase),
    headingLabel: suggestionHeadingLabel(source, assignment),
    eventName: source.name || 'Gestión',
    eventPlace: source.place || '',
    sectionName: sectionLabel(section, assignment),
    personName: planPersonName,
    sourcePersonName: assignment.person_name || planPersonName,
    role: assignment.role || '',
    draftStart: start,
    draftEnd: end,
    draftActivity: suggestionActivityLabel(source, assignment, section),
    existingLabel: existingTurnLabel(existing),
    hasExisting,
    status: 'pending',
  };
}

function suggestionGroupKey(suggestion) {
  return [
    suggestion.dayIso,
    suggestion.sourceType,
    suggestion.sourceId,
    suggestion.phaseLabel,
    suggestion.sectionName || suggestion.eventPlace || suggestion.phaseLabel,
    suggestion.draftStart,
    suggestion.draftEnd,
  ].map((item) => normalizeSearchText(item)).join('::');
}

function groupAiSuggestions(suggestions) {
  const groups = new Map();
  suggestions.forEach((suggestion) => {
    const key = suggestionGroupKey(suggestion);
    if (!groups.has(key)) groups.set(key, { id: key || `group-${groups.size}`, suggestions: [] });
    groups.get(key).suggestions.push(suggestion);
  });
  return [...groups.values()];
}

function suggestionGroupRoleAssignments(group) {
  const assignments = new Map();
  (group?.suggestions || []).forEach((suggestion) => {
    const role = String(suggestion.role || '').trim();
    if (!assignments.has(role)) assignments.set(role, []);
    assignments.get(role).push(suggestion.personName);
  });
  return [...assignments.entries()].map(([role, names]) => ({ role, names }));
}

function suggestionGroupStatus(group) {
  const suggestions = group?.suggestions || [];
  if (suggestions.some((suggestion) => suggestion.hasExisting && suggestion.status === 'pending')) return 'warning';
  if (suggestions.every((suggestion) => suggestion.status === 'applied')) return 'applied';
  if (suggestions.every((suggestion) => suggestion.status === 'discarded')) return 'discarded';
  return 'pending';
}

export default function PlanSemanalPage() {
  const { user } = useAuth();
  const canEdit = canWrite(user, 'rrhh.plan-semanal');
  const [offset, setOffset] = useState(0);
  const [weekBounds, setWeekBounds] = useState({ min: DEFAULT_WEEK_MIN_OFFSET, max: DEFAULT_WEEK_MAX_OFFSET });
  const [historicalWeeksEnabled, setHistoricalWeeksEnabled] = useState(false);
  const [people, setPeople] = useState([]);
  const [turns, setTurns] = useState(new Map());
  const [search, setSearch] = useState('');
  const [summaryFrom, setSummaryFrom] = useState(0);
  const [summaryTo, setSummaryTo] = useState(6);
  const [selected, setSelected] = useState(null);
  const [sideMode, setSideMode] = useState('person');
  const [selectedCells, setSelectedCells] = useState(new Set());
  const [selectionAnchor, setSelectionAnchor] = useState(null);
  const [editor, setEditor] = useState(null);
  const [statusMenu, setStatusMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [clipboard, setClipboard] = useState(null);
  const [personMarks, setPersonMarks] = useState([]);
  const [personJornals, setPersonJornals] = useState([]);
  const [personHourlyRates, setPersonHourlyRates] = useState([]);
  const [personMarksLoading, setPersonMarksLoading] = useState(false);
  const [publishedPlanDates, setPublishedPlanDates] = useState(new Set());
  const [sideWidth, setSideWidth] = useState(320);
  const [aiPanel, setAiPanel] = useState({
    active: false,
    dayIso: null,
    loading: false,
    error: '',
    suggestions: [],
  });
  const cellRefs = useRef(new Map());
  const workspaceRef = useRef(null);
  const inlineEditorRef = useRef(null);
  const mobileEditorRef = useRef(null);
  const inlineEditorFocusKeyRef = useRef('');
  const undoStack = useRef([]);
  const redoStack = useRef([]);
  const turnWriteInFlightRef = useRef(false);
  const turnSaveQueueRef = useRef(Promise.resolve());
  const pendingTurnWritesRef = useRef(0);

  function uniqueTurnPayloads(payloads) {
    const byCell = new Map();
    (payloads || []).forEach((payload) => {
      if (!payload?.persona || !payload?.fecha) return;
      const key = personnelTurnKey(payload.persona_external_id ?? payload.personnel_id ?? payload.persona_id, payload.fecha)
        || turnKey(payload.persona, payload.fecha);
      byCell.set(key, payload);
    });
    return [...byCell.values()];
  }

  async function saveTurnBatch(payloads) {
    const turnos = uniqueTurnPayloads(payloads);
    if (!turnos.length) return { ok: true, turnos };
    if (turnWriteInFlightRef.current) return { ok: false, busy: true, turnos };

    turnWriteInFlightRef.current = true;
    try {
      let lastError;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          await rrhhCorePost('/plan/turnos/lote', { turnos });
          return { ok: true, turnos };
        } catch (err) {
          lastError = err;
          const isDeadlock = /deadlock detected/i.test(String(err?.message || err));
          if (!isDeadlock || attempt === 2) throw err;
          await new Promise((resolve) => window.setTimeout(resolve, 160 * (attempt + 1)));
        }
      }
      throw lastError;
    } finally {
      turnWriteInFlightRef.current = false;
    }
  }

  function trackTurnWrite(promise) {
    pendingTurnWritesRef.current += 1;
    setSaving(true);
    return promise.finally(() => {
      pendingTurnWritesRef.current = Math.max(0, pendingTurnWritesRef.current - 1);
      if (pendingTurnWritesRef.current === 0) setSaving(false);
    });
  }

  function queueTurnBatch(payloads) {
    const turnos = uniqueTurnPayloads(payloads);
    if (!turnos.length) return Promise.resolve({ ok: true, turnos });
    const queued = turnSaveQueueRef.current
      .catch(() => {})
      .then(() => saveTurnBatch(turnos));
    turnSaveQueueRef.current = queued.catch(() => {});
    return trackTurnWrite(queued);
  }

  function resizeSidePanel(clientX) {
    const workspace = workspaceRef.current;
    if (!workspace) return;
    const bounds = workspace.getBoundingClientRect();
    const maximum = Math.min(560, Math.max(280, bounds.width - 520));
    setSideWidth(Math.min(maximum, Math.max(280, bounds.right - clientX)));
  }

  function startSideResize(event) {
    if (window.matchMedia('(max-width: 900px)').matches) return;
    event.preventDefault();
    const handleMove = (moveEvent) => resizeSidePanel(moveEvent.clientX);
    const handleEnd = () => {
      document.body.classList.remove('rrhh-plan-resizing');
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleEnd);
      window.removeEventListener('pointercancel', handleEnd);
    };
    document.body.classList.add('rrhh-plan-resizing');
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleEnd);
    window.addEventListener('pointercancel', handleEnd);
  }

  function resizeSideWithKeyboard(event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    event.stopPropagation();
    const direction = event.key === 'ArrowLeft' ? 1 : -1;
    const workspaceWidth = workspaceRef.current?.getBoundingClientRect().width || 1080;
    const maximum = Math.min(560, Math.max(280, workspaceWidth - 520));
    setSideWidth((current) => Math.min(maximum, Math.max(280, current + direction * 20)));
  }

  const days = useMemo(() => weekDays(offset), [offset]);
  const weekOptions = useMemo(() => Array.from({ length: weekBounds.max - weekBounds.min + 1 }, (_, index) => {
    const value = weekBounds.min + index;
    const optionDays = weekDays(value);
    return { value, label: weekLabel(optionDays, value) };
  }), [weekBounds]);

  function selectWeekOffset(value) {
    setOffset(value);
    if (!historicalWeeksEnabled) return;
    setWeekBounds((current) => {
      if (value <= current.min) return { min: current.min - HISTORICAL_WEEK_BATCH, max: current.max };
      if (value >= current.max) return { min: current.min, max: current.max + HISTORICAL_WEEK_BATCH };
      return current;
    });
  }

  function toggleHistoricalWeeks() {
    if (historicalWeeksEnabled) {
      setHistoricalWeeksEnabled(false);
      setWeekBounds({ min: DEFAULT_WEEK_MIN_OFFSET, max: DEFAULT_WEEK_MAX_OFFSET });
      setOffset(0);
      return;
    }

    setHistoricalWeeksEnabled(true);
    setWeekBounds((current) => ({
      min: Math.min(current.min, offset - HISTORICAL_WEEK_BATCH),
      max: Math.max(current.max, offset + HISTORICAL_WEEK_BATCH),
    }));
  }

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    Promise.all([
      loadPlanDirectory(),
      rrhhCoreGet('/configuracion').catch(() => []),
    ])
      .then(([personRows, configRows]) => {
        if (!active) return;
        setPublishedPlanDates(parsePublishedPlanDates(configRows));
        setPeople(personRows
          .filter(isActivePerson)
          .filter((person) => String(personOperationalRole(person) || '').trim())
          .sort((a, b) => roleSortValue(a) - roleSortValue(b)
            || personOperationalRole(a).localeCompare(personOperationalRole(b), 'es')
            || personDisplayName(a).localeCompare(personDisplayName(b), 'es')));
      })
      .catch((err) => setError(err.message || 'No se pudo cargar personal de RRHH'))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    if (!people.length) return () => { active = false; };
    rrhhCoreGet(`/plan/turnos?desde=${days[0].iso}&hasta=${days[6].iso}`)
      .then(async (rows) => {
        if (!active) return;
        const nextTurns = turnMapForDirectory(rows, people);
        const defaults = fixedSchedulePayloads(people, days, nextTurns);
        if (defaults.length) {
          const saved = await saveTurnBatch(defaults);
          if (saved.ok) {
            saved.turnos.forEach((turn) => {
              const person = people.find((candidate) => Number(candidate.id) === Number(turn.persona_id ?? turn.personnel_id ?? turn.persona_external_id));
              setTurnInMap(nextTurns, turn, person);
            });
          }
        }
        if (active) setTurns(nextTurns);
      })
      .catch((err) => setError(err.message || 'No se pudieron cargar turnos'))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [days, people]);

  useEffect(() => {
    if (!selected && people.length) {
      const initial = { personName: personDisplayName(people[0]), date: days[0].iso };
      setSelected(initial);
      setSelectedCells(new Set([cellId(initial.personName, initial.date)]));
      setSelectionAnchor(initial);
    }
  }, [days, people, selected]);

  useEffect(() => {
    if (!selected) return;
    const personExists = people.some((person) => personDisplayName(person) === selected.personName);
    const dayExists = days.some((day) => day.iso === selected.date);
    if (personExists && dayExists) return;
    if (!people.length) {
      setSelected(null);
      setSelectedCells(new Set());
      setSelectionAnchor(null);
      return;
    }
    const next = { personName: personDisplayName(people[0]), date: days[0].iso };
    setSelected(next);
    setSelectedCells(new Set([cellId(next.personName, next.date)]));
    setSelectionAnchor(next);
  }, [days, people, selected]);

  useEffect(() => {
    if (!editor) return;
    const focusKey = cellId(editor.personName, editor.date);
    if (inlineEditorFocusKeyRef.current === focusKey) return;
    inlineEditorFocusKeyRef.current = focusKey;
    window.setTimeout(() => {
      const input = activeEditorElement()?.querySelector('input');
      input?.focus();
      input?.select();
    }, 0);
  }, [editor]);

  useEffect(() => {
    if (!editor) inlineEditorFocusKeyRef.current = '';
  }, [editor]);

  useEffect(() => {
    function handlePointerDown(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      if (statusMenu && !target.closest('.rrhh-plan-status-menu')) {
        setStatusMenu(null);
      }
      if (!editor) return;
      if (target.closest('.rrhh-plan-inline-editor')) return;
      if (target.closest('.rrhh-plan-cell')) return;
      if (target.closest('.rrhh-plan-status-menu')) return;
      saveEditor();
    }

    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [editor, statusMenu]);

  function showToast(message) {
    setToast(message);
    window.setTimeout(() => setToast(''), 2400);
  }

  function activeEditorElement() {
    return window.matchMedia('(max-width: 640px)').matches ? mobileEditorRef.current : inlineEditorRef.current;
  }

  const visiblePeople = useMemo(() => {
    const query = normalizeSearchText(search);
    return people
      .filter((person) => {
        if (!query) return true;
        return normalizeSearchText(`${personDisplayName(person)} ${personOperationalRole(person)}`).includes(query);
      })
      .sort((a, b) => roleSortValue(a) - roleSortValue(b)
        || personOperationalRole(a).localeCompare(personOperationalRole(b), 'es')
        || personDisplayName(a).localeCompare(personDisplayName(b), 'es'));
  }, [people, search]);

  const selectedPerson = visiblePeople.find((person) => personDisplayName(person) === selected?.personName)
    || people.find((person) => personDisplayName(person) === selected?.personName)
    || null;
  const selectedDay = days.find((day) => day.iso === selected?.date) || days[0];
  const selectedTurn = selectedPerson ? turns.get(personTurnKey(selectedPerson, selectedDay.iso)) : null;
  const aiGroups = useMemo(() => groupAiSuggestions(aiPanel.suggestions), [aiPanel.suggestions]);
  const aiDay = days.find((day) => day.iso === aiPanel.dayIso) || selectedDay;

  useEffect(() => {
    if (!selectedPerson || sideMode !== 'person' || aiPanel.active) return undefined;
    let active = true;
    setPersonMarks([]);
    setPersonJornals([]);
    setPersonHourlyRates([]);
    setPersonMarksLoading(true);
    const month = monthRange(days[0].iso.slice(0, 7));
    Promise.all([
      rrhhCoreGet(`/marcas?persona_id=${selectedPerson.id}&desde=${days[0].iso}&hasta=${addIsoDays(days[6].iso, 1)}`),
      rrhhCoreGet(`/validacion/jornales?desde=${month.from}&hasta=${month.to}&persona_id=${selectedPerson.id}`),
      rrhhCoreGet(`/personal-tarifas/vigentes?desde=${month.from}&hasta=${month.to}&persona_id=${selectedPerson.id}`).catch(() => []),
    ])
      .then(([marks, jornals, hourlyRates]) => {
        if (!active) return;
        setPersonMarks(marks);
        setPersonJornals(jornals.filter((jornal) => normalizeSearchText(jornal.persona) === normalizeSearchText(personDisplayName(selectedPerson))));
        setPersonHourlyRates(Array.isArray(hourlyRates) ? hourlyRates : []);
      })
      .catch(() => {
        if (active) setPersonMarks([]);
      })
      .finally(() => {
        if (active) setPersonMarksLoading(false);
      });
    return () => {
      active = false;
    };
  }, [aiPanel.active, days, selectedPerson, sideMode]);

  function personByName(personName) {
    return people.find((person) => personDisplayName(person) === personName)
      || visiblePeople.find((person) => personDisplayName(person) === personName)
      || null;
  }

  function dayByDate(date) {
    return days.find((day) => day.iso === date) || null;
  }

  async function suggestDayWithAi(day = selectedDay) {
    if (!day) return;
    setEditor(null);
    setStatusMenu(null);
    setAiPanel({ active: true, dayIso: day.iso, loading: true, error: '', suggestions: [] });
    try {
      const rules = await loadRrhhAiRules();
      const sources = await loadParentPlanningSourcesForDay(day.iso, rules);
      const suggestions = sources.flatMap((source) => (
        eventAssignments(source)
          .filter((assignment) => shouldUseAssignmentForPlan(assignment, rules, source))
          .map((assignment) => buildAiSuggestion({ source, assignment, day, people, turns, rules }))
          .filter(Boolean)
      ));
      setAiPanel({ active: true, dayIso: day.iso, loading: false, error: '', suggestions });
      showToast(suggestions.length ? `${suggestions.length} sugerencias listas para revisar` : 'Sin sugerencias para ese día');
    } catch (err) {
      setAiPanel({
        active: true,
        dayIso: day.iso,
        loading: false,
        error: err?.status === 401 || err?.status === 403
          ? 'Tu sesión no tiene acceso a las asignaciones de Gestión.'
          : (err?.message || 'No se pudieron leer los datos de Gestión.'),
        suggestions: [],
      });
      showToast('No se pudieron generar sugerencias');
    }
  }

  function updateAiGroupDraft(group, patch) {
    const ids = new Set((group?.suggestions || []).map((suggestion) => suggestion.id));
    setAiPanel((prev) => ({
      ...prev,
      suggestions: prev.suggestions.map((suggestion) => (
        ids.has(suggestion.id) && suggestion.status === 'pending'
          ? { ...suggestion, ...patch }
          : suggestion
      )),
    }));
  }

  async function applyAiSuggestionGroup(group) {
    if (!canEdit) return;
    const applicable = (group?.suggestions || []).filter((suggestion) => suggestion.status === 'pending' && personByName(suggestion.personName));
    if (!applicable.length) return;
    const payloads = applicable.map((suggestion) => {
      const person = personByName(suggestion.personName);
      return {
        persona_external_id: person?.id || null,
        persona: suggestion.personName,
        rol_operativo: personOperationalRole(person) || 'Operador',
        fecha: suggestion.dayIso,
        estado: 'NORMAL',
        hora_inicio: normalizeTime(suggestion.draftStart) || null,
        hora_fin: normalizeTime(suggestion.draftEnd) || null,
        actividad_ubicacion: normalizePlanActivity(suggestion.draftActivity || suggestion.eventPlace || suggestion.eventName || 'OPERACION'),
        ubicacion: String(suggestion.eventPlace || '').trim() || null,
        modificado: true,
      };
    });
    const saved = await applyTurnPayloads(payloads, { message: applicable.length > 1 ? `${applicable.length} sugerencias aplicadas` : 'Sugerencia aplicada' });
    if (!saved) return;
    const appliedIds = new Set(applicable.map((suggestion) => suggestion.id));
    setAiPanel((prev) => ({
      ...prev,
      suggestions: prev.suggestions.map((suggestion) => (
        appliedIds.has(suggestion.id) ? { ...suggestion, status: 'applied', hasExisting: false, existingLabel: '' } : suggestion
      )),
    }));
    setSingleSelectedCell(applicable[0].personName, applicable[0].dayIso, { focus: true });
  }

  function discardAiSuggestionGroup(group) {
    const ids = new Set((group?.suggestions || []).map((suggestion) => suggestion.id));
    setAiPanel((prev) => ({
      ...prev,
      suggestions: prev.suggestions.map((suggestion) => (
        ids.has(suggestion.id) && suggestion.status === 'pending'
          ? { ...suggestion, status: 'discarded' }
          : suggestion
      )),
    }));
    showToast('Sugerencias descartadas');
  }

  function selectedCellRecords() {
    const cells = selectedCells.size ? [...selectedCells] : selected ? [cellId(selected.personName, selected.date)] : [];
    return cells
      .map((id) => {
        const separatorIndex = id.lastIndexOf('|');
        const personName = id.slice(0, separatorIndex);
        const date = id.slice(separatorIndex + 1);
        const person = personByName(personName);
        const day = dayByDate(date);
        return person && day ? { id, person, day, personName, date } : null;
      })
      .filter(Boolean)
      .sort((a, b) => {
        const personDelta = visiblePeople.findIndex((person) => personDisplayName(person) === a.personName)
          - visiblePeople.findIndex((person) => personDisplayName(person) === b.personName);
        if (personDelta) return personDelta;
        return days.findIndex((day) => day.iso === a.date) - days.findIndex((day) => day.iso === b.date);
      });
  }

  function focusCell(personName, date) {
    window.setTimeout(() => {
      cellRefs.current.get(cellId(personName, date))?.focus();
    }, 0);
  }

  function setSingleSelectedCell(personName, date, { focus = false } = {}) {
    const next = { personName, date };
    setSideMode('person');
    setSelected(next);
    setSelectedCells(new Set([cellId(personName, date)]));
    setSelectionAnchor(next);
    if (focus) focusCell(personName, date);
  }

  function selectCellRange(personName, fromDate, toDate, { focus = false } = {}) {
    setSelected({ personName, date: toDate });
    setSelectedCells(rangeSelectionForPerson(personName, days, fromDate, toDate));
    setSelectionAnchor({ personName, date: fromDate });
    if (focus) focusCell(personName, toDate);
  }

  async function handleCellClick(event, person, day) {
    const personName = personDisplayName(person);
    setStatusMenu(null);
    if (editor && (editor.personName !== personName || editor.date !== day.iso)) {
      if (!await saveEditor()) return;
    }
    if (event.shiftKey && selectionAnchor?.personName === personName) {
      selectCellRange(personName, selectionAnchor.date, day.iso, { focus: true });
      return;
    }
    setSingleSelectedCell(personName, day.iso);
  }

  function showPersonDetail(person) {
    const personName = personDisplayName(person);
    setAiPanel((prev) => ({ ...prev, active: false }));
    setSideMode('person');
    setSingleSelectedCell(personName, selected?.date || selectedDay.iso, { focus: true });
  }

  function showDayDetail(day) {
    setAiPanel((prev) => ({ ...prev, active: false }));
    setSideMode('day');
    setSelected((prev) => ({ personName: prev?.personName || personDisplayName(visiblePeople[0] || people[0] || {}), date: day.iso }));
    setSelectionAnchor((prev) => ({ personName: prev?.personName || personDisplayName(visiblePeople[0] || people[0] || {}), date: day.iso }));
  }

  async function openEditor(person, day) {
    if (!canEdit) return;
    if (editor) {
      if (editor.personName === personDisplayName(person) && editor.date === day.iso) return;
      if (!await saveEditor()) return;
    }
    const turn = turns.get(personTurnKey(person, day.iso));
    setStatusMenu(null);
    setSingleSelectedCell(personDisplayName(person), day.iso);
    setEditor(emptyEditor(person, day, turn, { forceNormal: true }));
  }

  async function handleCellContextMenu(event, person, day) {
    if (!canEdit) return;
    event.preventDefault();
    event.stopPropagation();
    const personName = personDisplayName(person);
    if (editor && (editor.personName !== personName || editor.date !== day.iso)) {
      if (!await saveEditor()) return;
    }
    setSingleSelectedCell(personName, day.iso, { focus: true });
    setStatusMenu({
      x: event.clientX,
      y: event.clientY,
      personName,
      date: day.iso,
    });
  }

  function handleCellKeyDown(event, person, day) {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      openEditor(person, day);
    }
  }

  function payloadSnapshot(person, day) {
    const current = turns.get(personTurnKey(person, day.iso));
    return shiftPayloadForTarget(current || { estado: 'VACIO' }, person, day);
  }

  function turnPayloadMatches(current, payload) {
    if (!current || !payload) return false;
    return [
      'persona',
      'fecha',
      'estado',
      'hora_inicio',
      'hora_fin',
      'actividad_ubicacion',
      'ubicacion',
      'persona_external_id',
      'rol_operativo',
    ].every((field) => String(current[field] || '') === String(payload[field] || ''))
      && JSON.stringify(plannedSegments(current)) === JSON.stringify(plannedSegments(payload));
  }

  function applyTurnPayloadsLocally(cleanPayloads, before, recordHistory) {
    const historyEntry = { before, after: cleanPayloads };
    setTurns((prev) => {
      const next = new Map(prev);
      cleanPayloads.forEach((payload) => {
        const person = personByName(payload.persona);
        setTurnInMap(next, payload, person);
      });
      return next;
    });
    if (recordHistory) {
      undoStack.current.push(historyEntry);
      redoStack.current = [];
    }
    return recordHistory ? historyEntry : null;
  }

  function rollbackTurnPayloads(cleanPayloads, before) {
    setTurns((prev) => {
      const next = new Map(prev);
      cleanPayloads.forEach((payload, index) => {
        const person = personByName(payload.persona);
        const key = personTurnKey(person || payload, payload.fecha);
        const current = next.get(key);
        if (turnPayloadMatches(current, payload)) setTurnInMap(next, before[index], person);
      });
      return next;
    });
  }

  function dropPendingHistoryEntry(entry) {
    if (!entry) return;
    const last = undoStack.current[undoStack.current.length - 1];
    if (last === entry) undoStack.current.pop();
  }

  async function applyTurnPayloads(payloads, { message = 'Turnos guardados', recordHistory = true, waitForServer = true } = {}) {
    const cleanPayloads = uniqueTurnPayloads(payloads);
    if (!cleanPayloads.length) return false;
    const before = cleanPayloads.map((payload) => {
      const person = personByName(payload.persona) || { nombre: payload.persona, rol_operativo: payload.rol_operativo };
      const day = dayByDate(payload.fecha) || { iso: payload.fecha };
      return payloadSnapshot(person, day);
    });
    setError('');
    const historyEntry = applyTurnPayloadsLocally(cleanPayloads, before, recordHistory);
    const persist = queueTurnBatch(cleanPayloads)
      .then((saved) => {
        if (!saved.ok) {
          showToast('Hay un guardado en curso');
          return false;
        }
        showToast(message);
        return true;
      })
      .catch((err) => {
        rollbackTurnPayloads(cleanPayloads, before);
        dropPendingHistoryEntry(historyEntry);
        setError(err.message || 'No se pudieron guardar los turnos');
        return false;
      });
    return waitForServer ? persist : true;
  }

  async function saveEditor(event, { waitForServer = false } = {}) {
    event?.preventDefault();
    if (!editor) return false;
    let payload;
    try { payload = editorPayload(editor); }
    catch (err) { setError(err.message); return false; }
    setEditor(null);
    focusCell(payload.persona, payload.fecha);
    return applyTurnPayloads([payload], { message: 'Turno guardado', waitForServer });
  }

  function cancelEditor() {
    const edited = editor;
    setEditor(null);
    if (edited) focusCell(edited.personName, edited.date);
  }

  function copySelectedShift() {
    const records = selectedCellRecords();
    const first = records[0];
    if (!first) return;
    const basePersonIndex = visiblePeople.findIndex((person) => personDisplayName(person) === first.personName);
    const baseDayIndex = days.findIndex((day) => day.iso === first.date);
    const items = records.map((record) => {
      const personIndex = visiblePeople.findIndex((person) => personDisplayName(person) === record.personName);
      const dayIndex = days.findIndex((day) => day.iso === record.date);
      const person = personByName(record.personName);
      const current = turns.get(personTurnKey(person || { nombre: record.personName }, record.date));
      return {
        rowOffset: personIndex - basePersonIndex,
        dayOffset: dayIndex - baseDayIndex,
        payload: shiftPayloadForTarget(current || { estado: 'VACIO' }, record.person, record.day),
      };
    });
    setClipboard({ type: 'range', items });
    showToast(items.length === 1 ? 'Turno copiado' : `${items.length} turnos copiados`);
  }

  async function pasteSelectedShift() {
    if (!clipboard || !canEdit) return;
    const records = selectedCellRecords();
    if (!records.length) return;
    let payloads = [];
    if (clipboard.type === 'range' && Array.isArray(clipboard.items)) {
      if (clipboard.items.length === 1 && records.length > 1) {
        payloads = records.map(({ person, day }) => shiftPayloadForTarget(clipboard.items[0].payload, person, day));
      } else {
        const base = selectedCoordinates();
        if (!base) return;
        payloads = clipboard.items
          .map((item) => {
            const person = visiblePeople[base.personIndex + item.rowOffset];
            const day = days[base.dayIndex + item.dayOffset];
            return person && day ? shiftPayloadForTarget(item.payload, person, day) : null;
          })
          .filter(Boolean);
      }
    } else {
      payloads = records.map(({ person, day }) => shiftPayloadForTarget(clipboard, person, day));
    }
    if (!payloads.length) return;
    await applyTurnPayloads(payloads, { message: payloads.length === 1 ? 'Turno pegado' : 'Turnos pegados' });
  }

  async function clearSelectedShifts() {
    if (!canEdit) return;
    const records = selectedCellRecords();
    if (!records.length) return;
    const payloads = records.map(({ person, day }) => shiftPayloadForTarget({ estado: 'VACIO' }, person, day));
    await applyTurnPayloads(payloads, { message: payloads.length === 1 ? 'Turno limpiado' : 'Turnos limpiados' });
  }

  async function restoreHistory(stackFrom, stackTo, message) {
    if (!canEdit || !stackFrom.current.length) return;
    const entry = stackFrom.current.pop();
    const saved = await applyTurnPayloads(entry.before, { message, recordHistory: false });
    if (saved) {
      stackTo.current.push(entry);
    } else {
      stackFrom.current.push(entry);
    }
  }

  async function undoLastChange() {
    await restoreHistory(undoStack, redoStack, 'Cambio deshecho');
  }

  async function redoLastChange() {
    if (!canEdit || !redoStack.current.length) return;
    const entry = redoStack.current.pop();
    const saved = await applyTurnPayloads(entry.after, { message: 'Cambio rehecho', recordHistory: false });
    if (saved) {
      undoStack.current.push(entry);
    } else {
      redoStack.current.push(entry);
    }
  }

  async function saveStatusForCell(status, target = statusMenu) {
    if (!target || !canEdit) return;
    const person = personByName(target.personName);
    const day = dayByDate(target.date);
    if (!person || !day) return;
    setStatusMenu(null);
    setEditor(null);
    const payload = shiftPayloadForTarget({ estado: status }, person, day);
    const saved = await applyTurnPayloads([payload], { message: `${STATUS_META[status]?.label || status} guardado` });
    if (saved) focusCell(target.personName, target.date);
  }

  function quickStatus(status) {
    if (!selectedPerson || !selectedDay || !canEdit) return;
    saveStatusForCell(status, {
      personName: personDisplayName(selectedPerson),
      date: selectedDay.iso,
    });
  }

  function selectedCoordinates() {
    const personIndex = visiblePeople.findIndex((person) => personDisplayName(person) === selected?.personName);
    const dayIndex = days.findIndex((day) => day.iso === selected?.date);
    if (personIndex === -1 || dayIndex === -1) return null;
    return { personIndex, dayIndex };
  }

  function moveSelection(rowDelta, dayDelta, { extend = false } = {}) {
    const coordinates = selectedCoordinates();
    if (!coordinates) return;
    const nextPersonIndex = Math.min(Math.max(coordinates.personIndex + rowDelta, 0), visiblePeople.length - 1);
    const nextDayIndex = Math.min(Math.max(coordinates.dayIndex + dayDelta, 0), days.length - 1);
    const personName = personDisplayName(visiblePeople[nextPersonIndex]);
    const date = days[nextDayIndex].iso;
    if (extend && rowDelta === 0 && selectionAnchor?.personName === selected?.personName) {
      selectCellRange(personName, selectionAnchor.date, date, { focus: true });
      return;
    }
    setSingleSelectedCell(personName, date, { focus: true });
  }

  useEffect(() => {
    function handleDocumentKeyDown(event) {
      const key = event.key.toLowerCase();
      const target = event.target instanceof Element ? event.target : null;
      const isEditableTarget = target?.closest('input, textarea, select, button, a');
      const isPlanCellTarget = target?.closest('.rrhh-plan-cell');
      const isSplitterTarget = target?.closest('.rrhh-plan-splitter');

      if (editor) {
        if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
          event.preventDefault();
          event.stopPropagation();
          const deltas = {
            ArrowUp: [-1, 0],
            ArrowDown: [1, 0],
            ArrowLeft: [0, -1],
            ArrowRight: [0, 1],
          };
          const [rowDelta, dayDelta] = deltas[event.key];
          saveEditor().then((saved) => {
            if (saved) moveSelection(rowDelta, dayDelta);
          });
          return;
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          cancelEditor();
          return;
        }
        if (event.key === 'Enter' && !event.target.closest('textarea')
          && (!event.target.closest('button') || event.target.closest('.rrhh-plan-segment-add'))) {
          event.preventDefault();
          saveEditor();
        }
        return;
      }

      if (isSplitterTarget) return;
      if (isEditableTarget && !isPlanCellTarget) return;

      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
        // La navegación con flechas pertenece exclusivamente a la grilla.
        // Si hay un turno seleccionado, mantiene ese foco lógico aunque el DOM
        // haya quedado en el documento después de cerrar el editor.
        if (!selected && !isPlanCellTarget) return;
        event.preventDefault();
        event.stopPropagation();
        if (event.shiftKey && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
          moveSelection(0, event.key === 'ArrowLeft' ? -1 : 1, { extend: true });
          return;
        }
        if (event.key === 'ArrowUp') moveSelection(-1, 0);
        if (event.key === 'ArrowDown') moveSelection(1, 0);
        if (event.key === 'ArrowLeft') moveSelection(0, -1);
        if (event.key === 'ArrowRight') moveSelection(0, 1);
        return;
      }

      if (event.key === 'Enter') {
        if (!selectedPerson || !selectedDay) return;
        event.preventDefault();
        openEditor(selectedPerson, selectedDay);
        return;
      }

      if (event.key === 'Escape') {
        setSelectedCells(selected ? new Set([cellId(selected.personName, selected.date)]) : new Set());
        return;
      }

      if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault();
        clearSelectedShifts();
        return;
      }

      if (!event.ctrlKey && !event.metaKey) return;
      if (key === 'c') {
        event.preventDefault();
        copySelectedShift();
      } else if (key === 'v') {
        event.preventDefault();
        pasteSelectedShift();
      } else if (key === 'z' && event.shiftKey) {
        event.preventDefault();
        redoLastChange();
      } else if (key === 'z') {
        event.preventDefault();
        undoLastChange();
      }
    }

    document.addEventListener('keydown', handleDocumentKeyDown);
    return () => document.removeEventListener('keydown', handleDocumentKeyDown);
  });

  function renderInlineEditor(extraClassName = '') {
    if (!editor) return null;
    const isMobile = extraClassName.includes('mobile');
    const updateSegment = (index, patch) => setEditor((prev) => prev && ({
      ...prev, status: 'NORMAL',
      segments: prev.segments.map((segment, i) => i === index ? { ...segment, ...patch } : segment),
    }));
    const addSegment = () => {
      const index = editor.segments.length;
      setEditor((prev) => ({ ...prev, segments: [...prev.segments, newEditorSegment(prev.segments.at(-1))] }));
      window.setTimeout(() => {
        const input = activeEditorElement()?.querySelector(`[data-segment-start="${index}"]`);
        input?.focus();
        input?.select();
      }, 0);
    };
    return (
      <form
        ref={isMobile ? mobileEditorRef : inlineEditorRef}
        className={`rrhh-plan-inline-editor ${extraClassName}`}
        onSubmit={saveEditor}
        onClick={(event) => event.stopPropagation()}
      >
        {isMobile && (
          <div className="rrhh-plan-inline-head">
            <span>{formatDisplayDate(editor.date)}</span>
            <button className="btn btn-ghost btn-sm" type="button" onClick={cancelEditor}>Cerrar</button>
          </div>
        )}
        {editor.segments.map((segment, index) => (
        <div className={`rrhh-plan-segment-editor${index ? ' additional' : ''}`} key={index}>
        <div className="rrhh-plan-sheet-fields">
          <input
            className="rrhh-plan-sheet-time rrhh-plan-sheet-start"
            value={segment.start}
            data-segment-start={index}
            inputMode="numeric"
            aria-label={`Entrada tramo ${index + 1}`}
            placeholder="09:00"
            onChange={(event) => updateSegment(index, { start: event.target.value })}
            onBlur={() => updateSegment(index, { start: normalizeTime(segment.start) || segment.start })}
          />
          <input
            className="rrhh-plan-sheet-time rrhh-plan-sheet-end"
            value={segment.end}
            inputMode="numeric"
            aria-label={`Salida tramo ${index + 1}`}
            placeholder="17:00"
            onChange={(event) => updateSegment(index, { end: event.target.value })}
            onBlur={() => updateSegment(index, { end: normalizeTime(segment.end) || segment.end })}
          />
          <label className="rrhh-plan-sheet-location-field">
          <span aria-hidden="true">📍</span>
          <input
            className="rrhh-plan-sheet-location"
            value={segment.location}
            aria-label={`Locación tramo ${index + 1}`}
            placeholder="Locación"
            onChange={(event) => updateSegment(index, { location: event.target.value })}
            onBlur={() => updateSegment(index, { location: segment.location.trim() })}
          />
          </label>
          <input
            className="rrhh-plan-sheet-activity"
            value={segment.activity}
            aria-label={`Tarea o referencia tramo ${index + 1}`}
            placeholder="LOGISTICA"
            onChange={(event) => updateSegment(index, { activity: event.target.value })}
            onBlur={() => updateSegment(index, { activity: segment.activity.trim().toUpperCase() })}
          />
        </div>
        {editor.segments.length > 1 && (
          <button type="button" className="rrhh-plan-segment-remove" title={`Quitar tramo ${index + 1}`}
            aria-label={`Quitar tramo ${index + 1}`} onClick={() => setEditor((prev) => ({
              ...prev, segments: prev.segments.filter((_, i) => i !== index),
            }))}><X size={12} aria-hidden="true" /></button>
        )}
        </div>
        ))}
        <button type="button" className="rrhh-plan-segment-add" title="Agregar tramo" aria-label="Agregar tramo"
          disabled={editor.segments.length >= 12} onClick={addSegment}><Plus size={14} aria-hidden="true" /></button>
        {isMobile && (
          <div className="rrhh-plan-inline-actions">
            <button className="btn btn-ghost btn-sm" type="button" onClick={cancelEditor}>Cancelar</button>
            <button className="btn btn-primary btn-sm" type="submit" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</button>
          </div>
        )}
      </form>
    );
  }

  const selectedDaySummary = useMemo(() => {
    const rows = visiblePeople
      .map((person) => {
        const personName = personDisplayName(person);
        const turn = turns.get(personTurnKey(person, selectedDay.iso));
        const shift = displayShift(turn);
        return { person, personName, turn, shift };
      })
      .filter(({ shift }) => shift.status !== 'VACIO');
    const normal = rows.filter(({ shift }) => shift.status === 'NORMAL');
    const hours = normal.reduce((total, { turn }) => total + plannedHours(turn), 0);
    return { rows, normal: normal.length, hours };
  }, [selectedDay.iso, turns, visiblePeople]);

  const exportablePeople = useMemo(
    () => visiblePeople.filter((person) => !isAdministrativeOperationalRole(person)),
    [visiblePeople],
  );

  const selectedDayExportRows = useMemo(
    () => selectedDaySummary.rows.filter(({ person }) => !isAdministrativeOperationalRole(person)),
    [selectedDaySummary.rows],
  );

  const selectedPersonSummary = useMemo(() => {
    if (!selectedPerson) return { worked: 0, pending: 0, daysWorked: 0, baseSalary: 0, monthTotal: 0, daily: [] };
    const daily = pairMarksByStartDay(personMarks).map((pair) => ({
      ...pair,
      hours: pairTotalHours(pair),
    }));
    const worked = personJornals.reduce((total, jornal) => total + Number(jornal.horas_trabajadas || 0), 0);
    const daysWorked = personJornals.filter((jornal) => Number(jornal.horas_trabajadas || 0) > 0).length;
    const agreedHours = Number(selectedPerson.horas_acordadas || 190);
    const fallbackHourlyRate = Number(selectedPerson.valor_hora || 0);
    const hourlyRate = rateForIsoDate(personHourlyRates, days[0].iso, fallbackHourlyRate);
    const monthTotal = personJornals.reduce((total, jornal) => (
      total + Number(jornal.horas_trabajadas || 0) * rateForIsoDate(personHourlyRates, jornal.fecha, fallbackHourlyRate)
    ), 0);
    return {
      worked,
      pending: Math.max(0, agreedHours - worked),
      daysWorked,
      baseSalary: hourlyRate * agreedHours,
      monthTotal,
      daily,
    };
  }, [days, personHourlyRates, personJornals, personMarks, selectedPerson]);

  function exportWeekSummary() {
    const from = Math.min(summaryFrom, summaryTo);
    const to = Math.max(summaryFrom, summaryTo);
    const peopleForExport = exportablePeople.map((person, index) => {
      const previous = exportablePeople[index - 1];
      return {
        source: person,
        name: personDisplayName(person),
        breakBefore: Boolean(previous && roleGroupForPerson(previous).key !== roleGroupForPerson(person).key),
      };
    });
    exportPlanRangeJpg({
      days,
      people: peopleForExport,
      rangeFrom: from,
      rangeTo: to,
      shiftFor: (person, day) => displayShift(turns.get(personTurnKey(person.source || { nombre: person.name }, day.iso))),
    });
    showToast('Resumen JPG generado');
  }

  function exportSelectedDay() {
    exportPlanDayJpg({ day: selectedDay, rows: selectedDayExportRows });
    showToast('Resumen del día generado');
  }

  async function publishSelectedDay() {
    if (!selectedDay?.iso) return;
    if (!canEdit) {
      showToast('No tenés permisos para publicar el día');
      return;
    }
    try {
      await turnSaveQueueRef.current.catch(() => {});
      const next = new Set(publishedPlanDates);
      next.add(selectedDay.iso);
      const value = [...next].sort();
      await rrhhCorePost('/configuracion', {
        clave: 'published_plan_dates',
        valor: value,
      });
      setPublishedPlanDates(new Set(value));
      showToast(`${formatDisplayDate(selectedDay.iso, { year: undefined })} publicado para el reloj`);
    } catch (err) {
      setError(err.message || 'No se pudo publicar el día');
    }
  }

  function renderAiPanel() {
    return (
      <div className="rrhh-plan-ai-panel">
        <div className="rrhh-native-day-head rrhh-plan-ai-sticky-head">
          <span>Sugerencias · {formatDisplayDate(aiDay.iso)}</span>
        </div>
        {aiPanel.loading && <div className="text-sm text-dim">Buscando asignaciones...</div>}
        {aiPanel.error && <div className="alert alert-error">{aiPanel.error}</div>}
        {!aiPanel.loading && !aiPanel.error && !aiGroups.length && (
          <div className="rrhh-native-empty">No encontramos personas de Gestión que coincidan con el plan para este día.</div>
        )}
        <div className="rrhh-plan-ai-list">
          {aiGroups.map((group) => {
            const suggestion = group.suggestions[0];
            const status = suggestionGroupStatus(group);
            const pending = group.suggestions.some((item) => item.status === 'pending');
            const peopleRows = suggestionGroupRoleAssignments(group);
            const existingItems = group.suggestions.filter((item) => item.hasExisting && item.status === 'pending');
            return (
              <article className={`rrhh-plan-ai-card ${status}`} key={group.id}>
                <div className="rrhh-plan-ai-people">
                  {peopleRows.map((row) => (
                    <div className={`rrhh-plan-ai-person-row${row.role ? '' : ' no-role'}`} key={`${group.id}-${row.role || 'sin-rol'}`}>
                      <strong>{row.names.join(', ')}</strong>
                      {row.role && <span>{row.role}</span>}
                    </div>
                  ))}
                </div>
                <div className="rrhh-plan-ai-context">
                  <span>{suggestion.headingLabel || suggestion.eventName || 'Gestión'}</span>
                  {(suggestion.sectionName || suggestion.eventPlace) && <span>{suggestion.sectionName || suggestion.eventPlace}</span>}
                </div>
                <div className="rrhh-plan-ai-fields">
                  <input
                    value={compactTime(suggestion.draftStart)}
                    inputMode="numeric"
                    aria-label="Entrada sugerida"
                    disabled={!pending}
                    onChange={(event) => updateAiGroupDraft(group, { draftStart: event.target.value })}
                    onBlur={(event) => updateAiGroupDraft(group, { draftStart: normalizeTime(event.target.value) })}
                  />
                  <span>-</span>
                  <input
                    value={compactTime(suggestion.draftEnd)}
                    inputMode="numeric"
                    aria-label="Salida sugerida"
                    disabled={!pending}
                    onChange={(event) => updateAiGroupDraft(group, { draftEnd: event.target.value })}
                    onBlur={(event) => updateAiGroupDraft(group, { draftEnd: normalizeTime(event.target.value) })}
                  />
                  <input
                    className="activity"
                    value={suggestion.draftActivity}
                    aria-label="Nombre para el plan"
                    disabled={!pending}
                    onChange={(event) => updateAiGroupDraft(group, { draftActivity: event.target.value })}
                    onBlur={(event) => updateAiGroupDraft(group, { draftActivity: normalizePlanActivity(event.target.value) })}
                  />
                </div>
                {existingItems.length > 0 && (
                  <p className="rrhh-plan-ai-existing">
                    En el plan: {existingItems.map((item) => `${item.personName}: ${item.existingLabel}`).join(' · ')}
                  </p>
                )}
                <div className="rrhh-plan-ai-actions">
                  <button className="btn btn-primary btn-sm" type="button" disabled={!pending || saving} onClick={() => applyAiSuggestionGroup(group)}>Marcar en el plan</button>
                  <button className="btn btn-ghost btn-sm" type="button" disabled={!pending} onClick={() => discardAiSuggestionGroup(group)}>Ignorar</button>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <RRHHNativeShell title="Plan semanal">
      {error && <div className="alert alert-error">{error}</div>}

      <section className="rrhh-plan-native">
        <div className="rrhh-native-toolbar rrhh-plan-toolbar">
            <label className="rrhh-plan-control rrhh-plan-week-control">
              <span>Semana</span>
              <div className="rrhh-plan-week-picker-row">
                <select className="form-input" value={offset} onChange={(event) => selectWeekOffset(Number(event.target.value))}>
                  {weekOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
                <button
                  className={`btn btn-ghost rrhh-plan-history-button${historicalWeeksEnabled ? ' active' : ''}`}
                  type="button"
                  onClick={toggleHistoricalWeeks}
                  aria-pressed={historicalWeeksEnabled}
                  title={historicalWeeksEnabled ? 'Volver al rango habitual de semanas' : 'Habilitar navegación histórica'}
                >
                  Histórico
                </button>
              </div>
            </label>
            <label className="rrhh-plan-control rrhh-plan-search-control">
              <span>Buscar persona o rol</span>
              <input
                className="form-input"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Nombre o rol: Lucas, referente..."
              />
            </label>
            <label className="rrhh-plan-control">
              <span>Resumen desde</span>
              <select className="form-input" value={summaryFrom} onChange={(event) => setSummaryFrom(Number(event.target.value))}>
                {days.map((day, index) => <option key={day.iso} value={index}>{day.long} {day.day}</option>)}
              </select>
            </label>
            <label className="rrhh-plan-control">
              <span>Resumen hasta</span>
              <select className="form-input" value={summaryTo} onChange={(event) => setSummaryTo(Number(event.target.value))}>
                {days.map((day, index) => <option key={day.iso} value={index}>{day.long} {day.day}</option>)}
              </select>
            </label>
            <button className="btn btn-primary rrhh-plan-export-button" type="button" onClick={exportWeekSummary}>Exportar resumen</button>
          {loading && <span className="text-sm text-dim rrhh-plan-loading">Cargando...</span>}
        </div>

        <div
          className="rrhh-plan-workspace"
          ref={workspaceRef}
          style={{ '--rrhh-plan-side-width': `${sideWidth}px` }}
        >
          <div className="rrhh-plan-main">
          <div className="rrhh-plan-table-wrap">
            <table className="rrhh-plan-table">
              <thead>
                <tr>
                  <th>Persona</th>
                  {days.map((day) => (
                    <th key={day.iso} className={day.iso === currentIsoDate() ? 'today' : ''}>
                      <div className="rrhh-plan-day-head-cell">
                        <button className="rrhh-plan-day-head-button" type="button" onClick={() => showDayDetail(day)}>
                          {day.long} {day.day}
                        </button>
                        <button
                          className="rrhh-plan-day-ai-button"
                          type="button"
                          title={`Ver sugerencias para ${day.long} ${day.day}`}
                          aria-label={`Ver sugerencias para ${day.long} ${day.day}`}
                          onClick={() => suggestDayWithAi(day)}
                        >
                          <span aria-hidden="true">🪄</span>
                        </button>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visiblePeople.map((person, personIndex) => {
                  const personName = personDisplayName(person);
                  const segmentCount = Math.max(1, ...days.map((day) => plannedSegments(turns.get(personTurnKey(person, day.iso))).length));
                  const rowHeight = Math.max(58 * segmentCount, editor?.personName === personName ? 90 * editor.segments.length + 28 : 0);
                  const previousPerson = visiblePeople[personIndex - 1];
                  const isGroupBreak = previousPerson && roleGroupForPerson(previousPerson).key !== roleGroupForPerson(person).key;
                  return (
                    <tr className={isGroupBreak ? 'group-break' : ''} key={person.id || personName} style={{ '--planned-row-height': `${rowHeight}px` }}>
                      <th>
                        <button className="rrhh-plan-person-button" type="button" onClick={() => showPersonDetail(person)}>
                          <strong>{personName}</strong>
                        </button>
                      </th>
                      {days.map((day) => {
                        const turn = turns.get(personTurnKey(person, day.iso));
                        const shift = displayShift(turn);
                        const isSelected = selectedCells.has(cellId(personName, day.iso));
                        const isEditing = editor?.personName === personName && editor?.date === day.iso;
                        return (
                          <td
                            key={day.iso}
                            className={isEditing ? 'editing' : ''}
                            onContextMenu={(event) => handleCellContextMenu(event, person, day)}
                          >
                            {isEditing ? renderInlineEditor() : (
                              <button
                                ref={(node) => {
                                  const id = cellId(personName, day.iso);
                                  if (node) cellRefs.current.set(id, node);
                                  else cellRefs.current.delete(id);
                                }}
                                className={`rrhh-plan-cell ${shift.className}${isSelected ? ' selected' : ''}`}
                                type="button"
                                onClick={(event) => handleCellClick(event, person, day)}
                                onDoubleClick={() => openEditor(person, day)}
                                onContextMenu={(event) => handleCellContextMenu(event, person, day)}
                                onKeyDown={(event) => handleCellKeyDown(event, person, day)}
                                title={canEdit ? 'Enter o doble click para editar. Click derecho para estado.' : 'Solo lectura'}
                              >
                    {shift.status === 'NORMAL' ? shift.segments.map((segment, index) => (
                      <span className="rrhh-plan-cell-shift" key={index}>
                        <span className="rrhh-plan-cell-shift-meta">
                          <span className="rrhh-plan-cell-hours">{segment.hours || '--:-- - --:--'}</span>
                          {segment.locationShort ? (
                            <>
                              <span className="rrhh-plan-cell-divider" aria-hidden="true">|</span>
                              <span className="rrhh-plan-cell-location">📍 {segment.locationShort}</span>
                            </>
                          ) : null}
                        </span>
                        <strong>{segment.label}</strong>
                      </span>
                                )) : (
                                  <>
                                    {shift.hours && <span>{shift.hours}</span>}
                                    <strong>{shift.label}</strong>
                                  </>
                                )}
                              </button>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="rrhh-plan-mobile-list">
            {days.map((day) => {
              const rows = visiblePeople
                .map((person) => ({ person, shift: displayShift(turns.get(personTurnKey(person, day.iso))) }))
                .filter(({ shift }) => shift.status === 'NORMAL');
              return (
                <article className="rrhh-native-card" key={day.iso}>
                  <div className="rrhh-native-day-head">
                    <span>{formatDisplayDate(day.iso, { year: undefined })}</span>
                    <span>{rows.length} turnos</span>
                  </div>
                  <div className="rrhh-native-list" style={{ marginTop: 10 }}>
                    {!rows.length && <span className="text-sm text-dim">Sin turnos normales.</span>}
                    {rows.map(({ person, shift }) => {
                      const personName = personDisplayName(person);
                      const isEditing = editor?.personName === personName && editor?.date === day.iso;
                      return (
                        <div className="rrhh-plan-mobile-turn" key={person.id || personName}>
                          <button className="rrhh-plan-mobile-row" type="button" onClick={() => openEditor(person, day)}>
                            <strong>{personName}</strong>
                            {shift.segments.map((segment, index) => <span key={index}>
                              {segment.hours}{segment.locationShort ? ` | 📍 ${segment.locationShort}` : ''} · {segment.label}
                            </span>)}
                          </button>
                          {isEditing && renderInlineEditor('mobile')}
                        </div>
                      );
                    })}
                  </div>
                </article>
              );
            })}
          </div>
        </div>

        <div
          className="rrhh-plan-splitter"
          role="separator"
          aria-label="Ajustar ancho del panel de detalle"
          aria-orientation="vertical"
          aria-valuemin="280"
          aria-valuemax="560"
          aria-valuenow={sideWidth}
          tabIndex={0}
          onPointerDown={startSideResize}
          onKeyDown={resizeSideWithKeyboard}
        >
          <span />
        </div>

        <aside className="rrhh-plan-side rrhh-native-card">
          {aiPanel.active ? renderAiPanel() : (
            <>
              <div className="rrhh-native-day-head">
                <span>{sideMode === 'day' ? 'Resumen del día' : 'Detalle'}</span>
                {sideMode === 'day' && (
                  <div className="rrhh-native-toolbar-group">
                    <button className="btn btn-ghost btn-sm" type="button" disabled={!canEdit || publishedPlanDates.has(selectedDay.iso)} onClick={publishSelectedDay}>
                      {publishedPlanDates.has(selectedDay.iso) ? 'Publicado' : 'Publicar día'}
                    </button>
                    <button className="btn btn-primary btn-sm" type="button" onClick={exportSelectedDay}>Exportar día</button>
                  </div>
                )}
              </div>
              {sideMode === 'day' ? (
                <div className="rrhh-plan-day-detail">
                  <div>
                    <h2>{formatDisplayDate(selectedDay.iso, { year: undefined })}</h2>
                    <p>{selectedDaySummary.normal} turnos normales · {Math.round(selectedDaySummary.hours * 10) / 10} h</p>
                  </div>
                  <div className="rrhh-plan-day-metrics">
                    <span><strong>{selectedDaySummary.rows.length}</strong> registros</span>
                    <span><strong>{selectedDaySummary.normal}</strong> jornadas</span>
                  </div>
                  <div className="rrhh-plan-day-list">
                    {selectedDaySummary.rows.length ? selectedDaySummary.rows.map(({ person, personName, shift }) => (
                      <button className="rrhh-plan-day-item" type="button" key={personName} onClick={() => showPersonDetail(person)}>
                        <strong>{personName}</strong>
                        <span>{shift.hours ? `${shift.hours} · ${shift.label}` : shift.label}</span>
                      </button>
                    )) : <div className="rrhh-native-empty">Sin registros para este día.</div>}
                  </div>
                </div>
              ) : selectedPerson ? (
            <div className="rrhh-plan-detail">
              <div>
                <h2>{personDisplayName(selectedPerson)}</h2>
                <p>{personOperationalRole(selectedPerson)}</p>
              </div>
              <div className="rrhh-plan-person-metrics">
                <span><small>Días trabajados</small><strong>{selectedPersonSummary.daysWorked}</strong></span>
                <span><small>Horas reales</small><strong>{formatHours(selectedPersonSummary.worked)} h</strong></span>
                <span><small>Horas pendientes</small><strong>{formatHours(selectedPersonSummary.pending)} h</strong></span>
                <span><small>Sueldo base</small><strong>{formatMoney(selectedPersonSummary.baseSalary)}</strong></span>
                <span><small>Total mes</small><strong>{formatMoney(selectedPersonSummary.monthTotal)}</strong></span>
              </div>
              <div className={`rrhh-plan-detail-shift ${displayShift(selectedTurn).className}`}>
                <span>{displayShift(selectedTurn).hours || displayShift(selectedTurn).status}</span>
                <strong>{displayShift(selectedTurn).label}</strong>
              </div>
              <div className="rrhh-plan-person-week-list">
                {personMarksLoading && <span className="text-sm text-dim">Cargando marcas reales...</span>}
                {!personMarksLoading && days.map((day) => {
                  const pairs = selectedPersonSummary.daily.filter((pair) => pair.dayKey === day.iso);
                  const worked = pairs.reduce((total, pair) => total + (pair.hours || 0), 0);
                  const shift = displayShift(turns.get(personTurnKey(selectedPerson, day.iso)));
                  return (
                    <div className="rrhh-plan-person-day" key={day.iso}>
                      <strong>{day.long} {day.day}</strong>
                      <span>{worked ? `${formatHours(worked)} h reales` : 'Sin marcas'} · {shift.hours || shift.label}</span>
                    </div>
                  );
                })}
              </div>
              {canEdit && (
                <>
                  <button className="btn btn-primary" type="button" onClick={() => openEditor(selectedPerson, selectedDay)}>Editar turno</button>
                  <div className="rrhh-plan-quick-actions">
                    {['LIBRE', 'AUSENTE', 'LICENCIA', 'SUSPENDIDO', 'VACIO'].map((status) => (
                      <button className="btn btn-ghost btn-sm" key={status} type="button" onClick={() => quickStatus(status)}>{status}</button>
                    ))}
                  </div>
                </>
              )}
            </div>
              ) : (
                <div className="rrhh-native-empty">Seleccioná un turno.</div>
              )}
            </>
          )}
        </aside>
        </div>
      </section>

      {statusMenu && (
        <div
          className="rrhh-plan-status-menu"
          style={{ left: `min(${statusMenu.x}px, calc(100vw - 220px))`, top: `min(${statusMenu.y}px, calc(100vh - 280px))` }}
          onClick={(event) => event.stopPropagation()}
        >
          <button
            className="rrhh-plan-status-option normal"
            type="button"
            onClick={() => {
              const person = personByName(statusMenu.personName);
              const day = dayByDate(statusMenu.date);
              if (person && day) openEditor(person, day);
            }}
          >
            <span>Editar jornada</span>
            <strong>Inicio · fin · actividad</strong>
          </button>
          <div className="rrhh-plan-status-separator" />
          {STATUS_CONTEXT_OPTIONS.map((option) => (
            <button
              className={`rrhh-plan-status-option ${STATUS_META[option.value]?.className || ''}`}
              key={option.value}
              type="button"
              onClick={() => saveStatusForCell(option.value)}
            >
              <span>{option.label}</span>
              <strong>{STATUS_META[option.value]?.label || option.value}</strong>
            </button>
          ))}
        </div>
      )}

      <div className={`rrhh-native-toast alert alert-success${toast ? ' visible' : ''}`}>{toast}</div>
    </RRHHNativeShell>
  );
}
